import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  Search,
  Bot,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { useAppDispatch } from 'hooks/useAppStore';
import { setSearchOpen } from 'app/providers/uiSlice';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { cn } from 'utils/cn';
import { isSchoolLearnPath, resolveLearnHref } from 'utils/learnerNavActive';
import { useStudentProfile } from 'schools/parashari/hooks/useStudentProfile';
import { resolvePrimarySchoolSlug } from 'utils/primarySchool';

type BottomNavItem = {
  id: string;
  label: string;
  shortLabel: string;
  to?: string;
  icon: LucideIcon;
  action?: 'search';
};

/**
 * Five primary destinations (symmetric: 2 + Search FAB + 2).
 * Secondary destinations live in the sidebar / hamburger menu.
 */
const BOTTOM_NAV_ITEMS: BottomNavItem[] = [
  {
    id: 'dashboard',
    label: learnerNav.dashboard,
    shortLabel: 'Home',
    to: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    id: 'learn',
    label: 'Learn',
    shortLabel: 'Learn',
    icon: BookOpen,
  },
  {
    id: 'search',
    label: 'Search',
    shortLabel: 'Search',
    icon: Search,
    action: 'search',
  },
  {
    id: 'ai-tutor',
    label: learnerNav.aiTutor,
    shortLabel: 'AskGuru',
    to: ROUTES.AI_TUTOR,
    icon: Bot,
  },
  {
    id: 'progress',
    label: learnerNav.progress,
    shortLabel: 'Progress',
    to: ROUTES.PROFILE,
    icon: TrendingUp,
  },
];

const TAB_BASE =
  'touch-manipulation relative flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-2xs font-semibold leading-tight tracking-wide transition-all duration-250 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface active:scale-[0.97]';

function isItemActive(item: BottomNavItem, pathname: string): boolean {
  if (item.id === 'learn') {
    return isSchoolLearnPath(pathname) || pathname === ROUTES.SADHANA;
  }
  if (!item.to) return false;
  if (item.to === ROUTES.DASHBOARD) return pathname === ROUTES.DASHBOARD;
  if (item.to === ROUTES.AI_TUTOR) return pathname.startsWith('/ai');
  if (item.to === ROUTES.PROFILE) {
    return pathname === ROUTES.PROFILE || pathname === ROUTES.SETTINGS;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function NavTab({
  item,
  active,
  onSearch,
  learnHref,
}: {
  item: BottomNavItem;
  active: boolean;
  onSearch: () => void;
  learnHref: string;
}) {
  const Icon = item.icon;

  if (item.action === 'search') {
    return (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-end px-0.5 pb-0.5">
        <button
          type="button"
          onClick={onSearch}
          className={cn(
            'touch-manipulation -mt-6 flex h-12 w-12 min-h-12 min-w-12 shrink-0 items-center justify-center rounded-full',
            'btn-gold border-2 border-surface shadow-gold',
            'transition-transform duration-250 ease-out hover:scale-105 active:scale-95',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
          )}
          aria-label="Open search"
        >
          <Icon className="h-5 w-5" strokeWidth={2.35} aria-hidden />
        </button>
        <span className="mt-1 max-w-full truncate text-2xs font-semibold leading-tight tracking-wide text-content-tertiary">
          {item.shortLabel}
        </span>
      </div>
    );
  }

  return (
    <Link
      to={item.id === 'learn' ? learnHref : item.to!}
      className={cn(
        TAB_BASE,
        active
          ? 'text-accent-strong'
          : 'text-content-tertiary hover:bg-surface-secondary hover:text-content-secondary',
      )}
      aria-current={active ? 'page' : undefined}
    >
      {active ? (
        <span
          className="absolute inset-x-2 top-0 h-0.5 rounded-full bg-accent"
          aria-hidden
        />
      ) : null}
      <span
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-250',
          active && 'bg-accent/15',
        )}
      >
        <Icon
          className={cn('h-5 w-5 shrink-0', active && 'text-accent-strong')}
          strokeWidth={active ? 2.35 : 2}
          aria-hidden
        />
      </span>
      <span className="max-w-full truncate">{item.shortLabel}</span>
    </Link>
  );
}

export function MobileBottomNav() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { profile } = useStudentProfile();
  const primarySchoolSlug = resolvePrimarySchoolSlug(profile);
  const learnHref = resolveLearnHref(location.pathname, primarySchoolSlug);

  const openSearch = () => dispatch(setSearchOpen(true));

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[var(--z-bottom-nav)] border-t border-border glass-panel pb-[env(safe-area-inset-bottom,0px)] md:hidden"
      aria-label="Primary navigation"
    >
      <div className="mx-auto flex max-w-lg items-end gap-0.5 px-1.5 pb-1 pt-1.5 sm:max-w-xl sm:px-2">
        {BOTTOM_NAV_ITEMS.map((item) => (
          <NavTab
            key={item.id}
            item={item}
            active={isItemActive(item, location.pathname)}
            onSearch={openSearch}
            learnHref={learnHref}
          />
        ))}
      </div>
    </nav>
  );
}
